import React, { useEffect, useState, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import { FiMapPin, FiSearch, FiChevronDown, FiChevronRight, FiCheck, FiX } from 'react-icons/fi';
import { dt, DashLang } from '../../utils/dashboardTranslations';

interface Area {
  id: number;
  name: string;
  name_en: string;
  governorate: string;
  governorate_en: string;
  delivery_fee: string | number;
  minimum_order: string | number;
  free_delivery_threshold: string | number | null;
  estimated_time: number | null;
  estimated_time_unit: string | null;
  business_days: boolean;
  is_active: boolean;
  sort_order: number;
}

interface GovernorateGroup {
  name: string;
  name_en: string;
  areas: Area[];
}

export default function DeliveryAreas() {
  const { store, lang } = useOutletContext<{ store: any; lang: DashLang }>();
  const [areas, setAreas] = useState<Area[]>([]);
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  // Mass fill
  const [showMassFill, setShowMassFill] = useState(false);
  const [massFill, setMassFill] = useState({ delivery_fee: '', minimum_order: '', free_delivery_threshold: '', estimated_time: '', estimated_time_unit: 'minutes', business_days: false });

  // Inline editing
  const [editingCell, setEditingCell] = useState<{ id: number; field: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editTimeUnit, setEditTimeUnit] = useState<string>('minutes');
  const [editBusinessDays, setEditBusinessDays] = useState(false);

  useEffect(() => {
    if (store?.id) loadAreas();
  }, [store?.id]);

  const loadAreas = async () => {
    const res = await api.get(`/stores/${store.id}/delivery-areas`);
    if (res.data.length === 0) {
      // Auto-seed Kuwait areas on first load
      try {
        await api.post(`/stores/${store.id}/delivery-areas/seed-kuwait`);
        const res2 = await api.get(`/stores/${store.id}/delivery-areas`);
        setAreas(res2.data);
      } catch {
        setAreas([]);
      }
    } else {
      setAreas(res.data);
    }
  };

  // Group areas by governorate
  const grouped = useMemo<GovernorateGroup[]>(() => {
    const groups: Record<string, GovernorateGroup> = {};
    const filtered = areas.filter((a) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (a.name || '').toLowerCase().includes(q) || (a.name_en || '').toLowerCase().includes(q) ||
        (a.governorate || '').toLowerCase().includes(q) || (a.governorate_en || '').toLowerCase().includes(q);
    });
    for (const area of filtered) {
      const key = area.governorate || 'other';
      if (!groups[key]) {
        groups[key] = { name: area.governorate || 'Other', name_en: area.governorate_en || 'Other', areas: [] };
      }
      groups[key].areas.push(area);
    }
    return Object.values(groups);
  }, [areas, search]);

  // Toggle single area
  const handleToggle = async (area: Area) => {
    try {
      await api.put(`/stores/${store.id}/delivery-areas/${area.id}`, { is_active: !area.is_active });
      setAreas((prev) => prev.map((a) => a.id === area.id ? { ...a, is_active: !a.is_active } : a));
    } catch {
      toast.error(dt('error_saving_area', lang));
    }
  };

  // Toggle entire governorate
  const handleGovernorateToggle = async (group: GovernorateGroup) => {
    const allActive = group.areas.every((a) => a.is_active);
    const newState = !allActive;
    const ids = group.areas.map((a) => a.id);
    try {
      await api.post(`/stores/${store.id}/delivery-areas/bulk-toggle`, { ids, is_active: newState });
      setAreas((prev) => prev.map((a) => ids.includes(a.id) ? { ...a, is_active: newState } : a));
    } catch {
      toast.error(dt('error_saving_area', lang));
    }
  };

  // Inline edit
  const startEdit = (id: number, field: string, currentValue: string | number | null, area?: Area) => {
    setEditingCell({ id, field });
    setEditValue(currentValue?.toString() || '');
    if (field === 'estimated_time' && area) {
      setEditTimeUnit(area.estimated_time_unit || 'minutes');
      setEditBusinessDays(area.business_days || false);
    }
  };

  const saveEdit = async () => {
    if (!editingCell) return;
    const { id, field } = editingCell;
    const payload: any = {};
    if (field === 'estimated_time') {
      payload[field] = editValue ? parseInt(editValue) : null;
      payload['estimated_time_unit'] = editTimeUnit;
      payload['business_days'] = editTimeUnit === 'days' ? editBusinessDays : false;
    } else {
      payload[field] = editValue ? parseFloat(editValue) : 0;
    }
    try {
      await api.put(`/stores/${store.id}/delivery-areas/${id}`, payload);
      setAreas((prev) => prev.map((a) => a.id === id ? { ...a, ...payload } : a));
      setEditingCell(null);
    } catch {
      toast.error(dt('error_saving_area', lang));
    }
  };

  const cancelEdit = () => setEditingCell(null);

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') saveEdit();
    if (e.key === 'Escape') cancelEdit();
  };

  // Selection
  const toggleSelect = (id: number) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]);
  };

  const toggleSelectGovernorate = (group: GovernorateGroup) => {
    const ids = group.areas.map((a) => a.id);
    const allSelected = ids.every((id) => selected.includes(id));
    if (allSelected) {
      setSelected((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSelected((prev) => Array.from(new Set([...prev, ...ids])));
    }
  };

  const toggleCollapse = (govName: string) => {
    setCollapsed((prev) => ({ ...prev, [govName]: !prev[govName] }));
  };

  // Mass fill
  const handleMassFill = async () => {
    if (selected.length === 0) {
      toast.error(lang === 'ar' ? 'اختر مناطق أولاً' : 'Select areas first');
      return;
    }
    const payload: any = { ids: selected };
    if (massFill.delivery_fee !== '') payload.delivery_fee = parseFloat(massFill.delivery_fee);
    if (massFill.minimum_order !== '') payload.minimum_order = parseFloat(massFill.minimum_order);
    if (massFill.free_delivery_threshold !== '') payload.free_delivery_threshold = parseFloat(massFill.free_delivery_threshold);
    if (massFill.estimated_time !== '') {
      payload.estimated_time = parseInt(massFill.estimated_time);
      payload.estimated_time_unit = massFill.estimated_time_unit;
      payload.business_days = massFill.estimated_time_unit === 'days' ? massFill.business_days : false;
    }

    setSaving(true);
    try {
      await api.post(`/stores/${store.id}/delivery-areas/mass-update`, payload);
      toast.success(lang === 'ar' ? 'تم تحديث المناطق' : 'Areas updated');
      setSelected([]);
      setShowMassFill(false);
      setMassFill({ delivery_fee: '', minimum_order: '', free_delivery_threshold: '', estimated_time: '', estimated_time_unit: 'minutes', business_days: false });
      loadAreas();
    } catch {
      toast.error(dt('error_saving_area', lang));
    }
    setSaving(false);
  };

  // Bulk enable/disable selected
  const handleBulkToggle = async (activate: boolean) => {
    try {
      await api.post(`/stores/${store.id}/delivery-areas/bulk-toggle`, { ids: selected, is_active: activate });
      setAreas((prev) => prev.map((a) => selected.includes(a.id) ? { ...a, is_active: activate } : a));
      setSelected([]);
      toast.success(lang === 'ar' ? 'تم التحديث' : 'Updated');
    } catch {
      toast.error(dt('error_saving_area', lang));
    }
  };

  if (!store) return null;

  const renderEditableCell = (area: Area, field: 'delivery_fee' | 'minimum_order' | 'free_delivery_threshold' | 'estimated_time', suffix?: string) => {
    const isEditing = editingCell?.id === area.id && editingCell?.field === field;
    const rawValue = area[field];
    const timeUnitLabel = (unit: string | null, bd: boolean) => {
      if (unit === 'days') return bd ? (lang === 'ar' ? 'يوم عمل' : 'business days') : (lang === 'ar' ? 'يوم' : 'days');
      if (unit === 'hours') return lang === 'ar' ? 'ساعة' : 'hours';
      return dt('min', lang);
    };
    const displayValue = field === 'estimated_time'
      ? (rawValue ? `${rawValue} ${timeUnitLabel(area.estimated_time_unit, area.business_days)}` : '-')
      : field === 'free_delivery_threshold'
        ? (rawValue && Number(rawValue) > 0 ? `${store.currency_symbol} ${Number(rawValue).toFixed(3)}` : '-')
        : `${store.currency_symbol} ${Number(rawValue || 0).toFixed(3)}`;

    if (isEditing) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            type="number"
            step={field === 'estimated_time' ? '1' : '0.001'}
            className="form-input"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleEditKeyDown}
            autoFocus
            style={{ width: field === 'estimated_time' ? 60 : 90, padding: '4px 8px', fontSize: 13 }}
          />
          {field === 'estimated_time' && (
            <>
              <select
                className="form-input"
                value={editTimeUnit}
                onChange={(e) => setEditTimeUnit(e.target.value)}
                style={{ width: 70, padding: '4px 4px', fontSize: 12 }}
              >
                <option value="minutes">{lang === 'ar' ? 'دقيقة' : 'min'}</option>
                <option value="hours">{lang === 'ar' ? 'ساعة' : 'hrs'}</option>
                <option value="days">{lang === 'ar' ? 'يوم' : 'days'}</option>
              </select>
              {editTimeUnit === 'days' && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <input type="checkbox" checked={editBusinessDays} onChange={(e) => setEditBusinessDays(e.target.checked)} />
                  {lang === 'ar' ? 'عمل' : 'biz'}
                </label>
              )}
            </>
          )}
          <button onClick={saveEdit} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--success)', padding: 2 }}><FiCheck size={14} /></button>
          <button onClick={cancelEdit} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: 2 }}><FiX size={14} /></button>
        </div>
      );
    }

    return (
      <span
        onClick={() => startEdit(area.id, field, rawValue, area)}
        style={{ cursor: 'pointer', padding: '2px 6px', borderRadius: 4, transition: 'background 0.15s' }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--gray-100)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        {displayValue}
      </span>
    );
  };

  return (
    <div>
      <div className="page-header">
        <h1>{dt('delivery_areas', lang)}</h1>
      </div>

      {areas.length > 0 && (
        <>
          {/* Search + bulk actions bar */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
              <FiSearch style={{ position: 'absolute', insetInlineStart: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)' }} />
              <input
                className="form-input"
                placeholder={dt('search_areas', lang)}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ paddingLeft: 36 }}
              />
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-sm btn-white" onClick={() => {
                const allIds = areas.map(a => a.id);
                setSelected(selected.length === allIds.length ? [] : allIds);
              }}>
                {selected.length === areas.length ? (lang === 'ar' ? 'إلغاء الكل' : 'Unselect All') : (lang === 'ar' ? 'تحديد الكل' : 'Select All')}
              </button>
              <button className="btn btn-sm btn-white" onClick={() => {
                const allCollapsed = grouped.every(g => collapsed[g.name]);
                const newState: Record<string, boolean> = {};
                grouped.forEach(g => { newState[g.name] = !allCollapsed; });
                setCollapsed(newState);
              }}>
                {grouped.every(g => collapsed[g.name]) ? (lang === 'ar' ? 'توسيع الكل' : 'Expand All') : (lang === 'ar' ? 'طي الكل' : 'Collapse All')}
              </button>
            </div>
            {selected.length > 0 && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>
                  {selected.length} {lang === 'ar' ? 'محدد' : 'selected'}
                </span>
                <button className="btn btn-sm btn-white" onClick={() => handleBulkToggle(true)}>
                  {dt('enable_selected', lang)}
                </button>
                <button className="btn btn-sm btn-white" onClick={() => handleBulkToggle(false)}>
                  {dt('disable_selected', lang)}
                </button>
                <button className="btn btn-sm btn-primary" onClick={() => setShowMassFill(!showMassFill)}>
                  {lang === 'ar' ? 'تعبئة جماعية' : 'Mass Fill'}
                </button>
              </div>
            )}
          </div>

          {/* Mass fill panel */}
          {showMassFill && selected.length > 0 && (
            <div style={{
              background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 8,
              padding: 16, marginBottom: 16, display: 'flex', gap: 12, alignItems: 'end', flexWrap: 'wrap'
            }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>
                  {dt('delivery_fee', lang)} ({store.currency_symbol})
                </label>
                <input
                  type="number" step="0.001" className="form-input"
                  value={massFill.delivery_fee}
                  onChange={(e) => setMassFill({ ...massFill, delivery_fee: e.target.value })}
                  style={{ width: 120, padding: '6px 10px' }}
                  placeholder="0.000"
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>
                  {dt('minimum_order', lang)} ({store.currency_symbol})
                </label>
                <input
                  type="number" step="0.001" className="form-input"
                  value={massFill.minimum_order}
                  onChange={(e) => setMassFill({ ...massFill, minimum_order: e.target.value })}
                  style={{ width: 120, padding: '6px 10px' }}
                  placeholder="0.000"
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>
                  {lang === 'ar' ? 'توصيل مجاني عند' : 'Free Delivery At'} ({store.currency_symbol})
                </label>
                <input
                  type="number" step="0.001" className="form-input"
                  value={massFill.free_delivery_threshold}
                  onChange={(e) => setMassFill({ ...massFill, free_delivery_threshold: e.target.value })}
                  style={{ width: 120, padding: '6px 10px' }}
                  placeholder="0.000"
                />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>
                  {dt('est_delivery_time', lang)}
                </label>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input
                    type="number" className="form-input"
                    value={massFill.estimated_time}
                    onChange={(e) => setMassFill({ ...massFill, estimated_time: e.target.value })}
                    style={{ width: 60, padding: '6px 10px' }}
                    placeholder="1"
                  />
                  <select
                    className="form-input"
                    value={massFill.estimated_time_unit}
                    onChange={(e) => setMassFill({ ...massFill, estimated_time_unit: e.target.value })}
                    style={{ width: 80, padding: '6px 6px', fontSize: 12 }}
                  >
                    <option value="minutes">{lang === 'ar' ? 'دقيقة' : 'min'}</option>
                    <option value="hours">{lang === 'ar' ? 'ساعة' : 'hrs'}</option>
                    <option value="days">{lang === 'ar' ? 'يوم' : 'days'}</option>
                  </select>
                  {massFill.estimated_time_unit === 'days' && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      <input type="checkbox" checked={massFill.business_days} onChange={(e) => setMassFill({ ...massFill, business_days: e.target.checked })} />
                      {lang === 'ar' ? 'أيام عمل' : 'Business days'}
                    </label>
                  )}
                </div>
              </div>
              <button className="btn btn-primary btn-sm" onClick={handleMassFill} disabled={saving}>
                {saving ? (lang === 'ar' ? 'جاري التحديث...' : 'Updating...') : (lang === 'ar' ? 'تطبيق' : 'Apply')}
              </button>
              <button className="btn btn-white btn-sm" onClick={() => setShowMassFill(false)}>
                {dt('cancel', lang)}
              </button>
            </div>
          )}

          {/* Governorate groups */}
          {grouped.map((group) => {
            const isCollapsed = collapsed[group.name];
            const allActive = group.areas.every((a) => a.is_active);
            const someActive = group.areas.some((a) => a.is_active);
            const govAreaIds = group.areas.map((a) => a.id);
            const allGovSelected = govAreaIds.every((id) => selected.includes(id));

            return (
              <div key={group.name} style={{ marginBottom: 16, border: '1px solid var(--gray-200)', borderRadius: 8, overflow: 'hidden' }}>
                {/* Governorate header */}
                <div
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                    background: 'var(--gray-50)', borderBottom: isCollapsed ? 'none' : '1px solid var(--gray-200)',
                    cursor: 'pointer', userSelect: 'none'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={allGovSelected}
                    onChange={() => toggleSelectGovernorate(group)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }} onClick={() => toggleCollapse(group.name)}>
                    {isCollapsed ? <FiChevronRight size={16} /> : <FiChevronDown size={16} />}
                    <strong style={{ fontSize: 15 }}>
                      {lang === 'ar' ? group.name : group.name_en}
                    </strong>
                    <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>
                      ({group.areas.length} {lang === 'ar' ? 'منطقة' : 'areas'})
                    </span>
                  </div>
                  <label
                    style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span style={{ color: 'var(--gray-500)' }}>
                      {allActive ? (lang === 'ar' ? 'مفعّل' : 'Enabled') : someActive ? (lang === 'ar' ? 'جزئي' : 'Partial') : (lang === 'ar' ? 'معطّل' : 'Disabled')}
                    </span>
                    <div
                      onClick={() => handleGovernorateToggle(group)}
                      style={{
                        width: 36, height: 20, borderRadius: 10,
                        background: allActive ? 'var(--primary, #2563eb)' : 'var(--gray-300)',
                        position: 'relative', transition: 'background 0.2s', cursor: 'pointer'
                      }}
                    >
                      <div style={{
                        width: 16, height: 16, borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: 2,
                        left: allActive ? 18 : 2,
                        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                      }} />
                    </div>
                  </label>
                </div>

                {/* Area rows */}
                {!isCollapsed && (
                  <div className="data-table" style={{ borderRadius: 0, border: 'none' }}>
                    <table>
                      <thead>
                        <tr>
                          <th style={{ width: 40 }}></th>
                          <th>{lang === 'ar' ? 'المنطقة' : 'Area'}</th>
                          <th style={{ width: 130 }}>{dt('delivery_fee', lang)}</th>
                          <th style={{ width: 130 }}>{dt('minimum_order', lang)}</th>
                          <th style={{ width: 130 }}>{lang === 'ar' ? 'توصيل مجاني' : 'Free At'}</th>
                          <th style={{ width: 120 }}>{lang === 'ar' ? 'وقت التوصيل' : 'ETA'}</th>
                          <th style={{ width: 80 }}>{dt('status', lang)}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.areas.map((area) => (
                          <tr key={area.id} style={{ opacity: area.is_active ? 1 : 0.5 }}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selected.includes(area.id)}
                                onChange={() => toggleSelect(area.id)}
                              />
                            </td>
                            <td>
                              <div>
                                <strong dir="rtl" style={{ fontSize: 13 }}>{area.name}</strong>
                                {area.name_en && (
                                  <span style={{ color: 'var(--gray-400)', fontSize: 12, marginInlineStart: 8 }}>{area.name_en}</span>
                                )}
                              </div>
                            </td>
                            <td>{renderEditableCell(area, 'delivery_fee')}</td>
                            <td>{renderEditableCell(area, 'minimum_order')}</td>
                            <td>{renderEditableCell(area, 'free_delivery_threshold')}</td>
                            <td>{renderEditableCell(area, 'estimated_time')}</td>
                            <td>
                              <div
                                onClick={() => handleToggle(area)}
                                style={{
                                  width: 36, height: 20, borderRadius: 10,
                                  background: area.is_active ? 'var(--primary, #2563eb)' : 'var(--gray-300)',
                                  position: 'relative', transition: 'background 0.2s', cursor: 'pointer'
                                }}
                              >
                                <div style={{
                                  width: 16, height: 16, borderRadius: '50%', background: '#fff',
                                  position: 'absolute', top: 2,
                                  left: area.is_active ? 18 : 2,
                                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                }} />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
